import React from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { Navigate } from 'react-router';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader';
import { commonStyles } from '~/modules/covenants/CovenantsDataForm/commonStyles.ts';
import { guaranteesClientPageStyles } from '~/modules/guarantees/form/client/styles.ts';
import ButtonLoadingIcon from '~/modules/guarantees/form/shared/ButtonLoadingIcon.tsx';
import ApplicationHeader from '../../shared/components/ApplicationHeader.tsx';
import ApplicationPageFrame from '../../shared/components/ApplicationPageFrame.tsx';
import GuaranteesManagerApplicationSidePanelLayout from '../shared/SidePanel/GuaranteesManagerApplicationSidePanelLayout.tsx';
import { MANAGER_APPROVAL_LETTER_TEXT } from '../step4/constants.ts';
import { useManagerApprovalLetterPdfPreview } from '../step4/useManagerApprovalLetterPdfPreview.ts';
import ManualSignGeneratedDocumentSection from './ManualSignGeneratedDocumentSection.tsx';
import ManualSignedApprovalLetterSection from './ManualSignedApprovalLetterSection.tsx';
import { useManagerApprovalLetterManualSignPage } from './useManagerApprovalLetterManualSignPage.ts';

const ManagerApprovalLetterManualSignPage: React.FC = () => {
  const {
    actionMenuAnchorEl,
    actionMenuDocumentId,
    deletingDocumentId,
    downloadingDocumentId,
    effectiveApplicationId,
    handleActionMenuClose,
    handleActionMenuOpen,
    handleAddDocument,
    handleCloseAlertModal,
    handleCloseAmAccessModal,
    handleCloseSuccessModal,
    handleDeleteUploadedDocument,
    handleDownloadDocument,
    handleSubmitByAmManager,
    headerProps,
    isAmAccessDenied,
    isAmAccessLoading,
    isApplicationLoading,
    isSubmitDisabled,
    isSubmittingByAmManager,
    manualSignedDocument,
    modalAlert,
    modalSuccess,
    redirectPath,
    setModalAlert,
    showGuaranteesRequestError,
    uploadingDocumentId,
  } = useManagerApprovalLetterManualSignPage();
  const { handleOpenPdf, isDocumentLoading, pdfUrl } = useManagerApprovalLetterPdfPreview({
    setModalAlert,
  });

  if (redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }

  if (isAmAccessLoading) {
    return <FullPageLoader />;
  }

  return (
    <ApplicationPageFrame
      modalAlert={modalAlert}
      modalSuccess={modalSuccess}
      onModalClose={isAmAccessDenied ? handleCloseAmAccessModal : handleCloseAlertModal}
      onSuccessModalClose={handleCloseSuccessModal}
    >
      <ApplicationHeader {...headerProps} />

      {isAmAccessDenied ? null : isApplicationLoading ? (
        <Box sx={commonStyles.centeredLoader}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={guaranteesClientPageStyles.applicationFlowStack}>
          <GuaranteesManagerApplicationSidePanelLayout
            applicationId={effectiveApplicationId}
            onError={showGuaranteesRequestError}
          >
            <ManualSignGeneratedDocumentSection
              isDocumentLoading={isDocumentLoading}
              pdfUrl={pdfUrl}
              onOpenPdf={handleOpenPdf}
            />

            <ManualSignedApprovalLetterSection
              document={manualSignedDocument}
              uploadingDocumentId={uploadingDocumentId}
              downloadingDocumentId={downloadingDocumentId}
              deletingDocumentId={deletingDocumentId}
              actionMenuAnchorEl={actionMenuAnchorEl}
              actionMenuDocumentId={actionMenuDocumentId}
              onAddDocument={handleAddDocument}
              onActionMenuOpen={handleActionMenuOpen}
              onActionMenuClose={handleActionMenuClose}
              onDownload={handleDownloadDocument}
              onDeleteUploadedDocument={handleDeleteUploadedDocument}
            />

            <Box sx={guaranteesClientPageStyles.flowPageActionsCentered}>
              <Button
                variant="contained"
                color="primary"
                type="button"
                sx={guaranteesClientPageStyles.generatedDocumentOpenButton}
                disabled={isSubmitDisabled}
                onClick={handleSubmitByAmManager}
                startIcon={isSubmittingByAmManager ? <ButtonLoadingIcon /> : undefined}
              >
                {MANAGER_APPROVAL_LETTER_TEXT.submitButton}
              </Button>
            </Box>
          </GuaranteesManagerApplicationSidePanelLayout>

        </Box>
      )}
    </ApplicationPageFrame>
  );
};

export default ManagerApprovalLetterManualSignPage;
